import { useEffect, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Markdown } from "../../components/Markdown";
import { PageRail } from "../../components/PageRail";
import { Card, PageHeader } from "../../components/ui";
import { docBySlug, headingsOf, sectionOf } from "../../docs";
import { useActiveSection, useRailFollows } from "../../lib/useActiveSection";
import { NotFoundPage } from "../NotFoundPage";
import { LIBRARY } from "./DocsLayout";

/** One document from the library, with "on this page" beside it. */
export function DocPage() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const doc = docBySlug(slug);
  const source = doc ? (doc.section ? sectionOf(doc.markdown, doc.section) : doc.markdown.replace(/^# .*\n/, "")) : "";
  const headings = headingsOf(source);

  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    target.scrollIntoView({ block: "start" });
    target.focus({ preventScroll: true });
  }, [hash, slug]);

  if (!doc) return <NotFoundPage />;
  return (
    <>
      <PageHeader title={doc.title} subtitle={doc.summary} />
      <div className="@min-[76rem]/docs:grid @min-[76rem]/docs:grid-cols-[minmax(0,1fr)_13rem] @min-[76rem]/docs:items-start @min-[76rem]/docs:gap-8">
        {headings.length > 1 && <DocRail headings={headings} />}
        {/* min-w-0: a wide code block scrolls inside the column rather than widening it (#421). The article takes the
            whole column - tables, code and figures use it - and Markdown holds prose to its reading measure. */}
        <Card className="min-w-0 @min-[76rem]/docs:col-start-1 @min-[76rem]/docs:row-start-1">
          <article data-testid="doc-article">
            <Markdown source={source} />
          </article>
        </Card>
      </div>
    </>
  );
}

/**
 * "On this page": marks the section being read and follows the scroll both
 * ways (#410). The hook's state lives here, not in DocPage (#417): a change
 * of section re-renders this list alone, never the document beside it.
 */
function DocRail({ headings }: { headings: ReturnType<typeof headingsOf> }) {
  const { hash } = useLocation();
  const active = useActiveSection(
    headings.map((h) => h.id),
    hash,
  );
  const railRef = useRef<HTMLElement>(null);
  useRailFollows(railRef, active);
  return (
    <PageRail label="On this page" railRef={railRef}>
      <ul className="text-sm @min-[76rem]/docs:mt-2">
        {headings.map((h) => {
          const current = active === h.id;
          return (
            <li key={h.id}>
              {/* A left rule, not a tint, marks the current entry - as on the release-notes rail. */}
              <Link
                to={`#${h.id}`}
                aria-current={current ? "location" : undefined}
                className={`block border-l-2 py-1 text-neutral-700 hover:underline dark:text-neutral-300 ${h.level === 3 ? "pl-5" : "pl-2.5"} ${current ? "border-brand-text font-semibold" : "border-transparent"}`}
              >
                {h.text}
              </Link>
            </li>
          );
        })}
      </ul>
    </PageRail>
  );
}

/** `/docs` itself: the shelf. */
export function DocsIndexPage() {
  return (
    <>
      <PageHeader title="Documentation" subtitle="Everything a customer needs to deploy, operate and sign in to Starsach - the same files that ship with your API key, kept current with every release." />
      <ul className="grid gap-4 sm:grid-cols-2">
        {LIBRARY.map((e) => (
          <li key={e.to}>
            <Link to={e.to} className="block h-full rounded-xl border border-neutral-200 bg-white p-4 transition-shadow hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900">
              <span className="block font-semibold">{e.title}</span>
              <span className="mt-1 block text-sm text-neutral-600 dark:text-neutral-400">{e.summary}</span>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
